import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import {
  useQueryParam, NumberParam, StringParam, withDefault
} from "use-query-params"
import { getUsers } from "requesters/userRequester"
import { roleCheck } from "utils/index"

import {
    Container, Row, Col,
} from "react-bootstrap"

import {
  ADMIN, OPERATIONS, ACCOUNTS, OPERATION_MANAGER, ACCOUNTS_MANAGER
} from "utils/constants"
import { getGear } from "requesters/gearRequester"
import CustBreadcrumb from "components/CustBreadcrumb"
import Loader from "components/Loader"
import { getGearHistoryRecords } from "../../../../../requesters/gearHistoryRecordRequester"
import { getBaseUrl, getCurrentUser, getCurrentRole } from "../../../reducers"
import * as actionTypes from "../../../actions/types"
import GearDetailsCard from "./GearDetailsCard";
import GearHistoryRecords from "./GearHistoryRecords";

export default function GearDetails() {
    const { id } = useParams()
    const gearId = parseInt(id, 10)
    const dispatch = useDispatch()
    const baseUrl = useSelector(getBaseUrl)
    const currentRole = useSelector(getCurrentRole)
    const currentUser = useSelector(getCurrentUser)

    const readOnly = !(currentRole.includes(ADMIN) || currentRole.includes(OPERATIONS) || currentRole.includes(ACCOUNTS) || currentUser.attributes.role === ADMIN)

    async function fetchData() {
      dispatch({ type: actionTypes.GEAR_REQUEST_START })
      const { response, error } = await getGear(baseUrl, id)
      if (response) dispatch({ type: actionTypes.GEAR_REQUEST_SUCCESS, data: response })
      if (error) dispatch({ type: actionTypes.GEAR_REQUEST_FAILURE })
    }

    // fetchData
    useEffect(() => {
        fetchData()
    }, [baseUrl, gearId])

    return (
      <Container fluid className="gear-details-page pb-5">
        <GearBreadcrumb readOnly={readOnly} />
        <GearBody readOnly={readOnly} />
      </Container>
    )
}

function GearBreadcrumb({ readOnly }) {
    const { id } = useParams()
    const gear = _.get(useSelector(state => state.data.gears.byId), id)
    const { requestGearState } = useSelector(state => state.manageGearPage)
    const gear_identifier = requestGearState ? "..." : _.get(gear, `attributes.gearIdentifier`, "error")

    if (readOnly) return <CustBreadcrumb links={[["Account Settings", "/settings/account-settings"], ["My Assets", "/settings/my-gear"]]} active={gear_identifier} />
    return <CustBreadcrumb links={[["Settings", "/settings"], ["Company Assets", "/settings/gears"]]} active={gear_identifier} />
}

function GearBody({ readOnly }) {
  const currentUser = useSelector(getCurrentUser)
  const { id } = useParams()
  const gear = _.get(useSelector(state => state.data.gears.byId), id)
  const { requestGearState } = useSelector(state => state.manageGearPage)

  const [users, setUsers] = useState({})
  const [gearHistoryRecords, setGearHistoryRecords] = useState([])
  const [requesting, setRequesting] = useState(false)
  const [listMeta, setListMeta] = useState(null)
  const [historyUserOptions, setHistoryUserOptions] = useState([])
  const [historyPage, setHistoryPage] = useQueryParam("history_page", withDefault(NumberParam, 1))
  const [historyPerPage, setHistoryPerPage] = useQueryParam("history_per_page", withDefault(StringParam, "10"))
  const [historyUserId, setHistoryUserId] = useQueryParam("history_user", NumberParam)
  const gearId = parseInt(id, 10)
  const baseUrl = useSelector(getBaseUrl)

  const fetchUserData = () => {
    getUsers(baseUrl, { by_status: "active" })
      .then(res => {
        if (res.status === 200) {
          setUsers(_.get(res.response, "user", {}))
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }

  useEffect(() => {
    fetchUserData()
  }, [baseUrl])

  useEffect(() => {
    setListMeta(null)
    setHistoryPage(1)
    setHistoryUserId(undefined)
    setHistoryUserOptions([])
  }, [gearId, setHistoryPage, setHistoryUserId])

  const historyUserFilterOptions = useMemo(
    () => historyUserOptions.map(u => [u.name, u.id]),
    [historyUserOptions]
  )

  const historyUserFilterName = useMemo(() => {
    if (historyUserId == null) return undefined
    const match = historyUserOptions.find(u => Number(u.id) === Number(historyUserId))
    return match?.name
  }, [historyUserId, historyUserOptions])

  const setHistoryUserFilter = useCallback(userId => {
    setHistoryUserId(userId)
    setHistoryPage(1)
  }, [setHistoryUserId, setHistoryPage])

  const fetchGearHistoryData = useCallback(() => {
    setRequesting(true)
    getGearHistoryRecords(baseUrl, gearId, {
      page: historyPage,
      per_page: historyPerPage,
      ...(historyUserId != null ? { by_history_user_id: historyUserId } : {})
    })
      .then(res => {
        if (res.status === 200 && res.response) {
          setGearHistoryRecords(_.orderBy(res.response.history, "attributes.createdAt", "desc"))
          const p = res.meta?.pagination
          if (p) {
            setListMeta({
              total_count: p.total_count,
              total_pages: p.total_pages,
              current_page: p.current_page,
              per_page: p.per_page,
              show_all: !!p.show_all
            })
          }
          const fu = res.meta?.history_filter_users
          if (Array.isArray(fu)) {
            setHistoryUserOptions(fu)
          }
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => setRequesting(false))
  }, [baseUrl, gearId, historyPage, historyPerPage, historyUserId])

  useEffect(() => {
    fetchGearHistoryData()
  }, [fetchGearHistoryData])

  if (requestGearState) return <Loader />
  if (!gear) return <h1>{`Asset with id: ${id} doesn't exist!`}</h1>

  const { attributes: { gearIdentifier } } = gear

  return (
    <>
      <h2 className="mb-1">{gearIdentifier}</h2>
      <Row>
        <Col md={8}>
          <GearDetailsCard currentUser={currentUser} readOnly={readOnly} users={users} onUpdateGear={fetchGearHistoryData} />
          <GearHistoryRecords
            readOnly={readOnly}
            requesting={requesting}
            gearHistoryRecords={gearHistoryRecords}
            gearId={gearId}
            listMeta={listMeta}
            historyPage={historyPage}
            setHistoryPage={setHistoryPage}
            historyPerPage={historyPerPage}
            setHistoryPerPage={setHistoryPerPage}
            setHistoryUserFilter={setHistoryUserFilter}
            historyUserFilterName={historyUserFilterName}
            historyUserFilterOptions={historyUserFilterOptions} />
        </Col>
      </Row>
    </>
  )
}
